import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import type { InspectResult, Verdict } from '@visionqc/shared';
import { api } from '../api/client';
import { useLive } from '../api/useLive';
import { useProducts } from '../api/useProducts';
import { hardwareApi, type HardwareStatus } from '../api/hardware';
import { ImageWithHeatmap } from '../components/ImageWithHeatmap';
import { StatTile } from '../components/common';
import { IpCameraSource, fetchSnapshotBlob } from '../components/liveCameras';

export function LiveView() {
  const { connected, last, recent } = useLive();
  const { products } = useProducts();
  const [productId, setProductId] = useState<number | undefined>();
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ipCamId, setIpCamId] = useState<number | null>(null);
  // The result of a capture done HERE. The live socket also delivers it, but a
  // manual capture must show its own answer immediately — waiting on the socket
  // round-trip (or a socket that never connected) is why "I don't see the
  // inspect" happened. This wins over the socket's `last` until the next capture.
  const [local, setLocal] = useState<InspectResult | null>(null);

  // Default to the first product once loaded.
  useEffect(() => {
    if (productId === undefined && products.length > 0) setProductId(products[0]!.id);
  }, [products, productId]);

  const throughput = recent.length; // inspections in the last 60s
  const shown = local ?? last;
  const verdict: Verdict | null = (shown?.verdict as Verdict | null) ?? null;

  const submit = useCallback(
    async (blob: Blob, filename: string) => {
      if (!productId) {
        setError('Select a product first');
        return;
      }
      setBusy(true);
      setError(null);
      try {
        // Show this capture's own result straight from the response; the socket
        // still broadcasts it to any other viewer.
        const result = await api.inspect(productId, blob, filename);
        setLocal(result);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [productId],
  );

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="view-title">Live inspection</h1>
        <div className="row" style={{ gap: 16 }}>
          <LineBadge />
          <div className="live-indicator">
            <span className="pulse" style={{ background: connected ? 'var(--good)' : 'var(--bad)' }} />
            {connected ? 'Live feed connected' : 'Reconnecting…'}
          </div>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1.2fr 1fr' }}>
        <div className="grid">
          <div className={`verdict-panel ${verdict ? verdict.toLowerCase() : 'idle'}`}>
            <div className={`verdict-text ${verdict ? verdict.toLowerCase() : 'idle'}`}>
              {verdict === 'UNSURE' ? '? UNSURE' : (verdict ?? 'STANDBY')}
            </div>
            {verdict === 'UNSURE' && (
              <div className="muted" style={{ fontSize: 16 }}>
                too close to call — queued for human review
              </div>
            )}
            {shown && (
              <div className="verdict-meta">
                score {shown.score.toFixed(3)} · {shown.latencyMs} ms
                {'modelVersion' in shown && shown.modelVersion ? ` · ${shown.modelVersion}` : ''}
              </div>
            )}
          </div>

          <div className="row">
            <StatTile label="Throughput / min" value={throughput} tone="var(--teal)" />
            <StatTile label="Last score" value={shown ? shown.score.toFixed(2) : '—'} tone="var(--accent)" />
            <StatTile
              label="Last latency"
              value={shown ? `${shown.latencyMs}ms` : '—'}
              tone="var(--violet)"
            />
          </div>

          {shown && (
            <div className="panel panel-tone" style={{ ['--tone']: 'var(--teal)' } as CSSProperties}>
              <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
                <strong>Last inspected image</strong>
                <label className="row" style={{ gap: 8, margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={showHeatmap}
                    onChange={(e) => setShowHeatmap(e.target.checked)}
                  />
                  Heatmap
                </label>
              </div>
              <ImageWithHeatmap
                imageKey={shown.imageKey}
                heatmapKey={shown.heatmapKey}
                showHeatmap={showHeatmap}
              />
            </div>
          )}
        </div>

        <div className="grid" style={{ alignContent: 'start' }}>
          <div className="panel panel-tone" style={{ ['--tone']: 'var(--accent)' } as CSSProperties}>
            <label>Product to inspect</label>
            <select
              value={productId ?? ''}
              onChange={(e) => setProductId(e.target.value ? Number(e.target.value) : undefined)}
              style={{ width: '100%' }}
            >
              {products.length === 0 && <option>No products — add one in Settings</option>}
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.sku})
                </option>
              ))}
            </select>
          </div>

          <DropZone onFile={(f) => submit(f, f.name)} busy={busy} />
          <WebcamCapture onCapture={(b) => submit(b, 'webcam.jpg')} busy={busy} />

          <div className="panel panel-tone" style={{ ['--tone']: 'var(--teal)' } as CSSProperties}>
            <strong>IP camera</strong>
            <p className="muted" style={{ fontSize: 12, margin: '6px 0 10px' }}>
              Inspect a still straight from a fixed camera on the network — the same cameras used for safety.
            </p>
            <IpCameraSource cameraId={ipCamId} onPick={setIpCamId} showConnect intervalMs={2000} />
            <button
              className="btn primary"
              style={{ marginTop: 10 }}
              disabled={busy || ipCamId == null}
              onClick={async () => {
                try {
                  const b = await fetchSnapshotBlob(ipCamId!);
                  await submit(b, 'ipcam.jpg');
                } catch (e) {
                  setError((e as Error).message);
                }
              }}
            >
              Inspect this frame
            </button>
          </div>
          {error && <div className="panel" style={{ color: 'var(--bad)' }}>{error}</div>}
        </div>
      </div>
    </div>
  );
}

/** Live line-tracking badge — only shown when the HAL is reachable and running. */
function LineBadge() {
  const [status, setStatus] = useState<HardwareStatus | null>(null);
  useEffect(() => {
    const tick = () => hardwareApi.status().then(setStatus).catch(() => setStatus(null));
    tick();
    const id = setInterval(tick, 2000);
    return () => clearInterval(id);
  }, []);

  const t = status?.ok ? status.tracking : undefined;
  if (!t || !t.running) return null;

  const missed = t.metrics.missed_deadline;
  const ok = t.watchdog_ok && missed === 0;
  return (
    <span className={`badge ${ok ? 'good' : 'bad'}`} title="Line product tracking">
      {ok ? '● TRACKING OK' : missed > 0 ? `● ${missed} DEADLINE MISSED` : '● WATCHDOG'}
    </span>
  );
}

function DropZone({ onFile, busy }: { onFile: (f: File) => void; busy: boolean }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="panel panel-tone" style={{ ['--tone']: 'var(--violet)' } as CSSProperties}>
      <strong>Manual test — upload</strong>
      <div
        className={`dropzone ${drag ? 'drag' : ''}`}
        style={{ marginTop: 12 }}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          const file = e.dataTransfer.files[0];
          if (file) onFile(file);
        }}
      >
        {busy ? 'Inspecting…' : 'Drop an image here, or click to browse'}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}

/**
 * Webcam capture with a device picker (Meet-style).
 *
 * Lists every video input — built-in camera, external USB (Logitech), and an
 * iPhone via Continuity Camera — and lets you switch between them live.
 *
 * Note: device *labels* are only exposed by the browser after permission has
 * been granted, so we request a stream first and enumerate afterwards.
 */
function WebcamCapture({ onCapture, busy }: { onCapture: (b: Blob) => void; busy: boolean }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string>('');
  const [on, setOn] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Optional face blur on the live preview (GDPR), mirroring the Live Safety view.
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const facesRef = useRef<Array<{ x: number; y: number; w: number; h: number }>>([]);
  const rafRef = useRef(0);
  const detBusyRef = useRef(false);
  const [blurFaces, setBlurFaces] = useState(false);
  const [blurStrength, setBlurStrength] = useState(6);
  const blurRef = useRef(false);
  const strengthRef = useRef(6);
  blurRef.current = blurFaces;
  strengthRef.current = blurStrength;

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  /** Acquire a stream (optionally for a specific device) and list the cameras. */
  const acquire = useCallback(
    async (wantedId?: string) => {
      setErr(null);
      try {
        stopStream();
        const stream = await navigator.mediaDevices.getUserMedia({
          video: wantedId ? { deviceId: { exact: wantedId } } : true,
        });
        streamRef.current = stream;
        setOn(true);

        // Labels are only populated once permission is granted.
        const all = await navigator.mediaDevices.enumerateDevices();
        const cams = all.filter((d) => d.kind === 'videoinput');
        setDevices(cams);

        const activeId = stream.getVideoTracks()[0]?.getSettings().deviceId ?? '';
        setDeviceId(wantedId ?? activeId);
      } catch (e) {
        setErr((e as Error).message);
        setOn(false);
      }
    },
    [stopStream],
  );

  /*
   * Attach the stream in an effect rather than inside acquire(): the <video> is
   * only mounted once `on` is true, so assigning srcObject during acquire()
   * would target a null ref and the preview would stay black.
   */
  useEffect(() => {
    const video = videoRef.current;
    if (on && video && streamRef.current) {
      video.srcObject = streamRef.current;
      void video.play().catch(() => undefined);
    }
  }, [on, deviceId]);

  // Re-enumerate when cameras are plugged in / a phone becomes available.
  useEffect(() => {
    const onChange = () => {
      if (on) void navigator.mediaDevices.enumerateDevices().then((all) =>
        setDevices(all.filter((d) => d.kind === 'videoinput')),
      );
    };
    navigator.mediaDevices?.addEventListener?.('devicechange', onChange);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', onChange);
  }, [on]);

  useEffect(() => stopStream, [stopStream]);

  // Draw the preview to a canvas and blur face regions (from the latest detection).
  const renderBlur = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas && video.videoWidth) {
      const W = video.videoWidth;
      const H = video.videoHeight;
      if (canvas.width !== W) {
        canvas.width = W;
        canvas.height = H;
      }
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.filter = 'none';
        ctx.drawImage(video, 0, 0, W, H);
        for (const f of facesRef.current) {
          const pad = 0.45 * Math.max(f.w * W, f.h * H);
          const x0 = Math.max(0, f.x * W - pad);
          const y0 = Math.max(0, f.y * H - pad);
          const x1 = Math.min(W, f.x * W + f.w * W + pad);
          const y1 = Math.min(H, f.y * H + f.h * H + pad);
          const bw = x1 - x0;
          const bh = y1 - y0;
          if (bw <= 0 || bh <= 0) continue;
          ctx.save();
          ctx.beginPath();
          ctx.rect(x0, y0, bw, bh);
          ctx.clip();
          ctx.filter = `blur(${Math.max(2, Math.round((Math.max(bw, bh) * strengthRef.current) / 40))}px)`;
          ctx.drawImage(video, x0, y0, bw, bh, x0, y0, bw, bh);
          ctx.drawImage(canvas, x0, y0, bw, bh, x0, y0, bw, bh);
          ctx.restore();
        }
      }
    }
    rafRef.current = requestAnimationFrame(renderBlur);
  }, []);

  // Detect faces on the preview a few times a second (server-side YuNet).
  const detectFaces = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || detBusyRef.current) return;
    detBusyRef.current = true;
    try {
      const c = document.createElement('canvas');
      c.width = video.videoWidth;
      c.height = video.videoHeight;
      c.getContext('2d')?.drawImage(video, 0, 0);
      const blob = await new Promise<Blob | null>((res) => c.toBlob(res, 'image/jpeg', 0.6));
      if (!blob) return;
      const form = new FormData();
      form.append('image', blob, 'f.jpg');
      form.append('kinds', 'person');
      form.append('blurFaces', '1');
      const r = await fetch('/api/safety/live', { method: 'POST', body: form });
      if (r.ok) {
        const j = (await r.json()) as { faces?: Array<{ x: number; y: number; w: number; h: number }> };
        facesRef.current = j.faces ?? [];
      }
    } catch {
      /* ignore — a dropped frame is fine */
    } finally {
      detBusyRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!(on && blurFaces)) {
      facesRef.current = [];
      cancelAnimationFrame(rafRef.current);
      return;
    }
    rafRef.current = requestAnimationFrame(renderBlur);
    const t = setInterval(() => void detectFaces(), 1200);
    return () => {
      cancelAnimationFrame(rafRef.current);
      clearInterval(t);
    };
  }, [on, blurFaces, renderBlur, detectFaces]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    canvas.toBlob((b) => b && onCapture(b), 'image/jpeg', 0.92);
  };

  return (
    <div className="panel panel-tone" style={{ ['--tone']: 'var(--teal)' } as CSSProperties}>
      <strong>Manual test — camera</strong>
      <div style={{ marginTop: 12 }}>
        {!on ? (
          <button className="btn" onClick={() => void acquire()}>
            Enable camera
          </button>
        ) : (
          <>
            {devices.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <label>Camera</label>
                <select
                  value={deviceId}
                  onChange={(e) => void acquire(e.target.value)}
                  style={{ width: '100%' }}
                >
                  {devices.map((d, i) => (
                    <option key={d.deviceId || i} value={d.deviceId}>
                      {d.label || `Camera ${i + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ marginBottom: 10 }}>
              <button
                className="btn sm"
                onClick={() => setBlurFaces((b) => !b)}
                style={{
                  fontWeight: 700,
                  borderColor: blurFaces ? 'var(--teal)' : 'var(--border)',
                  background: blurFaces ? 'color-mix(in srgb, var(--teal) 20%, transparent)' : undefined,
                  color: blurFaces ? 'var(--teal)' : 'var(--dim)',
                }}
                title="Blur every detected face on the preview (GDPR)"
              >
                Blur faces: {blurFaces ? 'ON' : 'OFF'}
              </button>
              {blurFaces && (
                <label className="row" style={{ gap: 6, fontSize: 13, marginTop: 8 }}>
                  Strength
                  <span className="muted" style={{ fontSize: 11 }}>light</span>
                  <input type="range" dir="ltr" min={1} max={10} step={1} value={blurStrength} onChange={(e) => setBlurStrength(Number(e.target.value))} style={{ direction: 'ltr' }} />
                  <span className="muted" style={{ fontSize: 11 }}>strong</span>
                  <span className="muted">{blurStrength}</span>
                </label>
              )}
            </div>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', borderRadius: 8, background: '#000', display: blurFaces ? 'none' : 'block' }}
            />
            <canvas
              ref={canvasRef}
              style={{ width: '100%', borderRadius: 8, background: '#000', display: blurFaces ? 'block' : 'none' }}
            />
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn primary" onClick={capture} disabled={busy}>
                {busy ? 'Inspecting…' : 'Capture & inspect'}
              </button>
              <button
                className="btn sm"
                onClick={() => {
                  stopStream();
                  setOn(false);
                }}
              >
                Stop
              </button>
            </div>
          </>
        )}
        {err && <div style={{ color: 'var(--bad)', marginTop: 8 }}>{err}</div>}
      </div>
    </div>
  );
}
